import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Folders, Archive, House } from '@phosphor-icons/react'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { useApp } from '@/contexts/AppContext'
import type { Thread, QANode, Answer } from '@/types'
import { storage } from '@/lib/storage'

export function HomePage() {
  const { t, i18n } = useTranslation()
  const { state } = useApp()

  // Main routes row (exclude tutorials as requested)
  const mainRouteCards: Array<{ id: 'entities' | 'archivio'; to: string; title: string; icon: React.ReactNode; className?: string }> = [
    { id: 'entities', to: '/entities', title: t('entities') || 'Entities', icon: <Folders size={40} />, className: 'bg-info-soft hover:bg-info-soft/80 border-info-border' },
    { id: 'archivio', to: '/archivio', title: t('archivio') || 'Archivio', icon: <Archive size={40} />, className: 'bg-archive/20 hover:bg-archive/30 border-archive' },
  ]

  // Expansion state for showing subRoutes
  const [expandedMain, setExpandedMain] = React.useState<{ entities: boolean; archivio: boolean }>({ entities: true, archivio: true })
  const [expandedEntities, setExpandedEntities] = React.useState<Record<string, boolean>>({})
  const [expandedArchived, setExpandedArchived] = React.useState<Record<string, boolean>>({})

  // Load persisted preferences on mount
  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [mainPref, entitiesPref, archivedPref] = await Promise.all([
          storage.getPreference('home.expandedMain'),
          storage.getPreference('home.expandedEntities'),
          storage.getPreference('home.expandedArchived')
        ])
        if (cancelled) return
        if (mainPref && typeof mainPref === 'object') {
          setExpandedMain({
            entities: (mainPref as any).entities !== false,
            archivio: (mainPref as any).archivio !== false,
          })
        }
        if (entitiesPref && typeof entitiesPref === 'object') {
          setExpandedEntities(entitiesPref as Record<string, boolean>)
        }
        if (archivedPref && typeof archivedPref === 'object') {
          setExpandedArchived(archivedPref as Record<string, boolean>)
        }
      } catch {
        // noop
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Persist preferences on change
  React.useEffect(() => { storage.savePreference('home.expandedMain', expandedMain) }, [expandedMain])
  React.useEffect(() => { storage.savePreference('home.expandedEntities', expandedEntities) }, [expandedEntities])
  React.useEffect(() => { storage.savePreference('home.expandedArchived', expandedArchived) }, [expandedArchived])

  // Compute background class for a thread card based on its last node status
  const getThreadBgClass = (thread: Thread): string => {
    const getLastNode = (nodeId: string): QANode | undefined => {
      const node = state.qaNodes[nodeId] as QANode | undefined
      if (!node) return undefined
      let last: QANode = node
      if (node.type === 'question' && node.answerId) {
        const child = getLastNode(node.answerId)
        if (child) last = child
      } else if (node.type === 'answer' && node.followUpQuestionIds.length > 0) {
        for (const qid of node.followUpQuestionIds) {
          const child = getLastNode(qid)
          if (child) last = child
        }
      }
      return last
    }

    const lastNode = getLastNode(thread.rootQuestionId)
    if (!lastNode) return 'bg-card'
    if (lastNode.type === 'answer') {
      return (lastNode as Answer).status === 'conclusive' ? 'bg-success-soft' : 'bg-error-soft'
    }
    // question: open if has no answer
    const isOpen = (lastNode as any).answerId ? false : true
    return isOpen ? 'bg-warning-soft' : 'bg-card'
  }

  const formatUpdated = (iso: string | undefined) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    try {
      return d.toLocaleDateString(i18n.language || undefined, { year: 'numeric', month: 'short', day: '2-digit' })
    } catch {
      return d.toLocaleDateString()
    }
  }

  // Build UI fragments with a single pass over entities
  const sortedEntities = React.useMemo(() => (
    Object.values(state.entities).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  ), [state.entities])

  const {
    nonArchivedCards,
    archivedCards,
    nonArchivedThreadsSections,
    archivedThreadsSections,
  } = React.useMemo(() => {
    const nonArchivedCards: React.ReactNode[] = []
    const archivedCards: React.ReactNode[] = []
    const nonArchivedThreadsSections: React.ReactNode[] = []
    const archivedThreadsSections: React.ReactNode[] = []

    sortedEntities.forEach((entity) => {
      const isArchived = entity.isArchived
      const basePath = isArchived ? `/archivio/${entity.id}` : `/entities/${entity.id}`
      const expandedMap = isArchived ? expandedArchived : expandedEntities
      const setExpandedMap = isArchived ? setExpandedArchived : setExpandedEntities

      // Entity card
      const entityCard = (
        <Link key={entity.id} to={basePath} className="shrink-0">
          <Card className={cn(
            'relative h-24 min-w-24 w-auto px-3 rounded-xl border transition-colors flex items-center justify-center text-center bg-card hover:bg-accent/10 border-border whitespace-nowrap'
          )}>
            {/* Toggle entity subroutes (threads) */}
            <div className="absolute bottom-1.5 right-1.5" onClick={(e)=>{ e.preventDefault(); e.stopPropagation() }}>
              <Checkbox
                checked={expandedMap[entity.id] !== false}
                onCheckedChange={(c)=> setExpandedMap(prev => ({ ...prev, [entity.id]: Boolean(c) }))}
                aria-label={`Toggle subroutes for ${entity.name}`}
              />
            </div>
            <div className="text-sm font-medium">{entity.name}</div>
          </Card>
        </Link>
      )

      if (isArchived) archivedCards.push(entityCard)
      else nonArchivedCards.push(entityCard)

      // Threads section for this entity (if expanded)
      const isExpanded = expandedMap[entity.id] !== false
      if (!isExpanded) return
      const threads = entity.threadIds
        .map(id => state.threads[id])
        .filter((t): t is Thread => Boolean(t))

      const section = (
        <div key={entity.id} className="min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <Link to={basePath} className="text-xs font-semibold hover:underline truncate">
              {entity.name}
            </Link>
          </div>
          <div className="overflow-x-auto pb-1">
            <div className="flex gap-2 pr-2">
              {threads.length === 0 && (
                <div className="text-xs text-muted-foreground py-6">
                  {t('noThreadsYet')}
                </div>
              )}
              {threads.map(thread => (
                <Link key={thread.id} to={`${basePath}/threads/${thread.id}`} className="shrink-0">
                  <Card className={cn(
                    'h-24 min-w-24 w-auto px-3 rounded-xl border transition-colors flex items-center justify-center text-center whitespace-nowrap',
                    getThreadBgClass(thread)
                  )}>
                    <div className="flex flex-col items-center">
                      <div className="text-sm font-medium" title={thread.title}>
                        {thread.title || '—'}
                      </div>
                      <div className="text-[13px] text-muted-foreground mt-1">
                        {t('updated')}: {formatUpdated(thread.updatedAt)}
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )

      if (isArchived) archivedThreadsSections.push(section)
      else nonArchivedThreadsSections.push(section)
    })

    return { nonArchivedCards, archivedCards, nonArchivedThreadsSections, archivedThreadsSections }
  }, [sortedEntities, expandedEntities, expandedArchived, t, state.threads])

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <House size={18} />
        <h1 className="text-lg font-semibold">{t('home') || 'Home'}</h1>
      </div>
      {/* Row 1: Main routes */}
      <div className="overflow-x-auto pb-1">
        <div className="flex gap-2 pr-2">
          {mainRouteCards.map((card, idx) => (
            <div key={idx} className="shrink-0">
              <Card
                className={cn(
                  "relative group h-32 min-w-32 w-auto px-4 flex items-center justify-center text-center border transition-colors rounded-xl whitespace-nowrap cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  card.className
                )}
                role="button"
                tabIndex={0}
                onClick={() => setExpandedMain(prev => ({ ...prev, [card.id]: !(prev[card.id] !== false ? true : false) }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setExpandedMain(prev => ({ ...prev, [card.id]: !(prev[card.id] !== false ? true : false) }))
                  }
                }}
              >
                {/* Toggle subroutes checkbox */}
                <div className="absolute bottom-1.5 right-1.5" onClick={(e)=>{ e.preventDefault(); e.stopPropagation() }}>
                  <Checkbox
                    checked={expandedMain[card.id] !== false}
                    onCheckedChange={(c)=> setExpandedMain(prev => ({ ...prev, [card.id]: Boolean(c) }))}
                    aria-label={`Toggle subroutes for ${card.title}`}
                  />
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <div className="rounded-full p-2 bg-background/60 group-hover:bg-background/80 transition-colors">
                    {card.icon}
                  </div>
                  <div className="text-sm font-medium">{card.title}</div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Entities (subRoutes) wrapped with a single border matching main route */}
  {expandedMain.entities !== false && nonArchivedCards.length > 0 && (
        <div className="mt-6 border border-info-border rounded-xl p-3">
          <div className="mb-2 text-sm font-semibold">{t('entities')}</div>
          <div className="overflow-x-auto pb-1">
            <div className="flex gap-2 pr-2">
              {nonArchivedCards}
            </div>
          </div>

          {/* For each entity, list its threads */}
          <div className="mt-4 space-y-4">{nonArchivedThreadsSections}</div>
        </div>
      )}

      {/* Archivio subRoutes wrapped with a single border matching main route */}
  {expandedMain.archivio !== false && archivedCards.length > 0 && (
        <div className="mt-6 space-y-4 border border-archive rounded-xl p-3">
          {/* Archived Entities row */}
          <div>
            <div className="mb-2 text-sm font-semibold">{t('archivio')}</div>
            <div className="overflow-x-auto pb-1">
              <div className="flex gap-2 pr-2">
                {archivedCards}
              </div>
            </div>
          </div>

          {/* For each archived entity, its threads */}
          {archivedThreadsSections}
        </div>
      )}
    </div>
  )
}

export default HomePage
